import {
  getHostnameTenantSlug,
  getTenantCustomDomain,
  getTenantPortalLoginRedirectUrl,
  isDevEnv,
  isSelectiveAiDomains,
  isUniversalLoginHost,
} from 'common/HelperFunctions'
import {USER_TYPES} from 'common/constants'

type LoginResult = {
  tenant?: string
  accessToken?: string
  userType?: string
}

type RedirectOptions = {
  path?: string
  searchParams?: Record<string, string>
}

/**
 * Redirect from universal login host to the tenant workspace subdomain.
 * Returns true when a redirect was initiated.
 */
export const redirectToTenantWorkspaceIfNeeded = (
  tenant: string,
  accessToken: string,
  options?: RedirectOptions
): boolean => {
  if (!isSelectiveAiDomains()) return false
  if (!isUniversalLoginHost()) return false
  if (!tenant || !accessToken) return false

  const currentSlug = getHostnameTenantSlug()
  if (currentSlug && currentSlug === tenant) return false

  const path = options?.path ?? '/dashboard'
  const url = getTenantPortalLoginRedirectUrl(tenant, accessToken, path, options?.searchParams)
  window.location.replace(url)
  return true
}

/**
 * Post-login navigation for selective-AI universal login and legacy parent redirects.
 * Students stay in the new portal; parents still go to the legacy portal until
 * the parent end is converted.
 * Returns true when a full-page redirect was initiated.
 */
export const handlePostLoginNavigation = (
  result: LoginResult,
  options?: RedirectOptions
): boolean => {
  const {tenant, accessToken, userType} = result
  if (!accessToken || isDevEnv()) return false

  if (isSelectiveAiDomains()) {
    return redirectToTenantWorkspaceIfNeeded(tenant ?? '', accessToken, {
      path: options?.path ?? '/dashboard',
      searchParams: options?.searchParams ?? {step: '0'},
    })
  }

  if (userType === USER_TYPES.PARENT) {
    window.location.replace(`//${getTenantCustomDomain()}?rauth_token=${accessToken}`)
    return true
  }

  return false
}
